import type { ReactNode } from 'react'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { DesignImage } from 'src/shared/components/Design'
import { cn } from 'src/shared/components/ui/utils'
import { getQuoteItemThumbnailUrl } from 'src/shared/utils/quoteItemThumbnail'
import {
  firstTrimmedUrl,
  itemBrandingIsOtherColour,
  itemDecorationDesign,
  itemMockupUrls,
} from '../utils'

/**
 * Best visual for a quote line: logo placed on the product, then a generated
 * mockup, then the plain catalogue photo. Branding made for another colour of
 * the product is skipped, so a colour change shows the new colour's photo.
 */
export const ProposalItemVisual = ({
  item,
  alt,
  className,
  imgClassName,
  preferProductPhoto = false,
}: {
  item: QuoteItem_
  alt: string
  className?: string
  imgClassName?: string
  /** Alternative cards show the product; the mockup section shows the logo on it. */
  preferProductPhoto?: boolean
}) => {
  const otherColour = itemBrandingIsOtherColour(item)
  const mockup = otherColour ? undefined : itemMockupUrls(item)[0]
  const design = otherColour ? undefined : itemDecorationDesign(item)
  const productUrl = firstTrimmedUrl([
    design?.image_url,
    getQuoteItemThumbnailUrl(item),
  ])
  const logoUrl = firstTrimmedUrl([design?.design_url])

  let visual: ReactNode = <div className="h-full w-full" />
  if (preferProductPhoto && productUrl) {
    visual = (
      <img
        src={productUrl}
        alt={alt}
        className={cn('h-full w-full object-contain', imgClassName)}
      />
    )
  } else if (design && productUrl && logoUrl) {
    visual = (
      <DesignImage
        logo_url={logoUrl}
        image_url={productUrl}
        design={design}
        containerClassName="absolute inset-0 h-full w-full"
        imageClassName="rounded-none object-contain"
      />
    )
  } else if (mockup) {
    visual = (
      <img
        src={mockup}
        alt={alt}
        className={cn('h-full w-full object-cover', imgClassName)}
      />
    )
  } else if (productUrl) {
    visual = (
      <img
        src={productUrl}
        alt={alt}
        className={cn('h-full w-full object-contain', imgClassName)}
      />
    )
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>{visual}</div>
  )
}
